import { createTheme, alpha } from '@mui/material';
import type {} from '@mui/x-data-grid/themeAugmentation';
import { IMPERIUM_PALETTE, BOOSTER_PALETTE, BOOSTER_BLUE_PALETTE } from './palette';
import type { BrandId } from './brand';
import { withAccent } from './withAccent';

export { withAccent };

// ─── Imperium Dual Theme ──────────────────────────────────────────────────────
// Dark: deep slate, golden accent, glassmorphism
// Light: clean white/slate-50, same golden accent, soft shadows
// brand: 'imperium' (dorado, default) | 'booster' (verde) | 'booster-blue' (azul) — pruebas

export function buildTheme(mode: 'light' | 'dark' = 'dark', brand: BrandId = 'imperium', accentOverride?: string) {
  const isDark = mode === 'dark';
  const baseP = brand === 'booster' ? BOOSTER_PALETTE
          : brand === 'booster-blue' ? BOOSTER_BLUE_PALETTE
          : IMPERIUM_PALETTE;
  // White-label: si el tenant tiene color propio, derivamos la paleta desde él.
  const P = accentOverride ? withAccent(baseP as typeof BOOSTER_PALETTE, accentOverride) : baseP;
  // Refinamientos visuales "sala de control" solo para la marca Booster (CRM).
  // Imperium (sitio público) queda con su estética actual.
  const isBooster = brand !== 'imperium';

  // ── Mode tokens ──────────────────────────────────────────────────────────────
  // Dark: usa la paleta Imperium (misma que el sitio público) — negro dominante
  const bg = isDark
    ? { base: P.bg.base, paper: P.bg.paper, elevated: P.bg.elevated, glass: P.bg.glass }
    : { base: '#f0f2f5', paper: '#ffffff',  elevated: '#fafafa',  glass: 'rgba(255,255,255,0.92)' };

  const text = isDark
    ? { primary: P.text.primary, secondary: P.text.secondary, disabled: P.text.disabled, inverse: P.text.inverse }
    : { primary: '#1c2128', secondary: '#57606a', disabled: '#8c959f', inverse: '#ffffff' };

  const border = isDark
    ? {
        subtle:  P.border.subtle,
        default: P.border.default,
        strong:  P.border.strong,
        golden:  P.border.golden,
      }
    : {
        subtle:  'rgba(208,215,222,0.7)',
        default: 'rgba(208,215,222,1)',
        strong:  'rgba(175,184,193,1)',
        golden:  'rgba(184,149,111,0.25)',
      };

  const shadow = isDark
    ? P.shadow
    : {
        sm:  '0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04)',
        md:  '0 4px 6px rgba(0,0,0,0.05), 0 2px 4px rgba(0,0,0,0.05)',
        lg:  '0 10px 15px rgba(0,0,0,0.05), 0 4px 6px rgba(0,0,0,0.04)',
        xl:  '0 20px 25px rgba(0,0,0,0.06), 0 10px 10px rgba(0,0,0,0.03)',
        glow:'0 0 20px rgba(184,149,111,0.10)',
      };

  // ── Accent tokens ─────────────────────────────────────────────────────────
  const golden       = isDark ? P.brand.golden    : P.brand.goldenMid;
  const goldenLight  = isDark ? P.brand.goldenLight: P.brand.golden;
  /**
   * En CLARO estos cinco eran DORADOS FIJOS: la variante clara se escribió cuando el modo
   * claro era exclusivo de Imperium, así que tenía cableado su acento. Cualquier otra marca
   * que entrara en claro —Booster, o un tenant con su color— veía sus botones y bordes en
   * dorado. Ahora se derivan de la marca activa, igual que en oscuro. (20/08/2026)
   *
   * El claro usa el tono MEDIO como base (más saturado, para que se lea sobre blanco) y
   * opacidades más bajas en los rellenos, porque sobre fondo claro rinden más.
   */
  const goldenSubtle = isDark ? P.brand.goldenSubtle : alpha(P.brand.goldenMid, 0.10);
  const goldenBorder = isDark ? P.brand.goldenBorder : alpha(P.brand.goldenMid, 0.22);
  const goldenGlow   = isDark ? P.brand.goldenGlow   : alpha(P.brand.goldenMid, 0.18);
  const goldenGrad   = isDark ? P.gradients.golden : `linear-gradient(135deg, ${P.brand.goldenMid} 0%, ${P.brand.goldenDark} 100%)`;
  const goldenGradSoft = isDark ? P.gradients.goldenSoft : `linear-gradient(135deg, ${P.brand.golden} 0%, ${P.brand.goldenMid} 100%)`;

  // ── Shadows array (25 entries) ────────────────────────────────────────────
  const shadows25: import('@mui/material').Shadows = isDark
    ? [
        'none',
        P.shadow.sm,
        '0 2px 6px rgba(0,0,0,0.45)',
        '0 3px 10px rgba(0,0,0,0.5)',
        P.shadow.md,
        '0 6px 20px rgba(0,0,0,0.55)',
        '0 8px 24px rgba(0,0,0,0.6)',
        '0 10px 28px rgba(0,0,0,0.6)',
        P.shadow.lg,
        '0 12px 36px rgba(0,0,0,0.65)',
        '0 14px 40px rgba(0,0,0,0.65)',
        '0 16px 44px rgba(0,0,0,0.7)',
        P.shadow.xl,
        '0 20px 52px rgba(0,0,0,0.7)',
        '0 24px 56px rgba(0,0,0,0.75)',
        '0 28px 60px rgba(0,0,0,0.75)',
        '0 32px 64px rgba(0,0,0,0.8)',
        '0 36px 68px rgba(0,0,0,0.8)',
        '0 40px 72px rgba(0,0,0,0.85)',
        '0 44px 76px rgba(0,0,0,0.85)',
        '0 48px 80px rgba(0,0,0,0.85)',
        '0 52px 84px rgba(0,0,0,0.9)',
        '0 56px 88px rgba(0,0,0,0.9)',
        '0 60px 92px rgba(0,0,0,0.9)',
        '0 64px 96px rgba(0,0,0,0.95)',
      ]
    : [
        'none',
        '0 1px 2px rgba(0,0,0,0.07)',
        '0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04)',
        '0 2px 4px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',
        '0 4px 6px rgba(0,0,0,0.05), 0 2px 4px rgba(0,0,0,0.04)',
        '0 5px 8px rgba(0,0,0,0.05), 0 3px 5px rgba(0,0,0,0.04)',
        '0 6px 10px rgba(0,0,0,0.05), 0 4px 6px rgba(0,0,0,0.04)',
        '0 7px 12px rgba(0,0,0,0.05), 0 4px 6px rgba(0,0,0,0.04)',
        '0 8px 15px rgba(0,0,0,0.05), 0 4px 6px rgba(0,0,0,0.04)',
        '0 10px 18px rgba(0,0,0,0.05), 0 5px 8px rgba(0,0,0,0.04)',
        '0 12px 20px rgba(0,0,0,0.05), 0 6px 8px rgba(0,0,0,0.04)',
        '0 14px 22px rgba(0,0,0,0.05), 0 6px 10px rgba(0,0,0,0.04)',
        '0 16px 24px rgba(0,0,0,0.05), 0 8px 10px rgba(0,0,0,0.04)',
        '0 18px 28px rgba(0,0,0,0.06), 0 8px 12px rgba(0,0,0,0.04)',
        '0 20px 30px rgba(0,0,0,0.06), 0 10px 12px rgba(0,0,0,0.04)',
        '0 22px 32px rgba(0,0,0,0.06), 0 10px 14px rgba(0,0,0,0.04)',
        '0 24px 36px rgba(0,0,0,0.07), 0 12px 14px rgba(0,0,0,0.04)',
        '0 26px 38px rgba(0,0,0,0.07), 0 12px 16px rgba(0,0,0,0.04)',
        '0 28px 40px rgba(0,0,0,0.07), 0 14px 16px rgba(0,0,0,0.04)',
        '0 30px 44px rgba(0,0,0,0.08), 0 14px 18px rgba(0,0,0,0.04)',
        '0 32px 48px rgba(0,0,0,0.08), 0 16px 18px rgba(0,0,0,0.04)',
        '0 34px 50px rgba(0,0,0,0.08), 0 16px 20px rgba(0,0,0,0.04)',
        '0 36px 52px rgba(0,0,0,0.09), 0 18px 20px rgba(0,0,0,0.04)',
        '0 38px 54px rgba(0,0,0,0.09), 0 18px 22px rgba(0,0,0,0.04)',
        '0 40px 56px rgba(0,0,0,0.09), 0 20px 22px rgba(0,0,0,0.04)',
      ];

  return createTheme({
    palette: {
      mode,
      primary: {
        main:         golden,
        light:        goldenLight,
        dark:         P.brand.goldenDark,
        contrastText: isDark ? P.brand.onAccent : '#ffffff',
      },
      secondary: {
        main:         isDark ? P.brand.goldenMid : P.brand.goldenDark,
        light:        golden,
        dark:         P.brand.goldenDark,
        contrastText: isDark ? P.brand.onAccent : '#ffffff',
      },
      success:  { main: '#22c55e', dark: '#16a34a', light: '#bbf7d0', contrastText: '#0d1117' },
      warning:  { main: '#f59e0b', dark: '#d97706', light: '#fef3c7', contrastText: '#0d1117' },
      error:    { main: '#ef4444', dark: '#dc2626', light: '#fee2e2', contrastText: '#ffffff' },
      info:     { main: '#3b82f6', dark: '#2563eb', light: '#dbeafe', contrastText: '#ffffff' },
      grey:     P.grey,
      background: {
        default: bg.base,
        paper:   bg.paper,
      },
      text: {
        primary:   text.primary,
        secondary: text.secondary,
        disabled:  text.disabled,
      },
      divider: border.subtle,
      action: {
        hover:              goldenSubtle,
        selected:           goldenSubtle,
        disabledBackground: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
        disabled:           text.disabled,
      },
    },

    typography: {
      fontFamily: '"Inter", "Public Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      h1: { fontWeight: 800, lineHeight: 1.2,  letterSpacing: '-0.02em' },
      h2: { fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.015em' },
      h3: { fontWeight: 700, lineHeight: 1.3,  letterSpacing: '-0.01em' },
      h4: { fontWeight: 700, lineHeight: 1.4,  letterSpacing: '-0.005em' },
      h5: { fontWeight: 700, lineHeight: 1.5 },
      h6: { fontWeight: 700, lineHeight: 1.56 },
      body1: { lineHeight: 1.6 },
      body2: { lineHeight: 1.57 },
      caption: { lineHeight: 1.5, letterSpacing: '0.02em' },
      overline: { letterSpacing: '0.08em', fontWeight: 700 },
    },

    shape: { borderRadius: 10 },

    shadows: shadows25,

    components: {

      // ── CssBaseline ──────────────────────────────────────────────────────────
      MuiCssBaseline: {
        styleOverrides: {
          html: { scrollBehavior: 'smooth' },
          body: {
            backgroundColor: bg.base,
            backgroundImage: isDark
              ? `radial-gradient(ellipse at 20% 0%, ${alpha(P.brand.golden, 0.05)} 0%, transparent 55%),
                 radial-gradient(ellipse at 80% 100%, ${alpha(P.brand.golden, 0.04)} 0%, transparent 50%)`
              : `radial-gradient(ellipse at 20% 0%, ${alpha(P.brand.goldenMid, 0.06)} 0%, transparent 55%),
                 radial-gradient(ellipse at 80% 100%, ${alpha(P.brand.goldenMid, 0.03)} 0%, transparent 50%)`,
            transition: 'background-color 0.25s ease',
          },
          // Scrollbar: fina emerald en Booster (CRM); oculta en Imperium (público).
          ...(isBooster ? {
            '*::-webkit-scrollbar': { width: 10, height: 10 },
            '*::-webkit-scrollbar-track': { background: 'transparent' },
            '*::-webkit-scrollbar-thumb': {
              background: alpha(golden, 0.30), borderRadius: 8,
              border: '3px solid transparent', backgroundClip: 'padding-box',
            },
            '*::-webkit-scrollbar-thumb:hover': {
              background: alpha(golden, 0.5), backgroundClip: 'padding-box',
            },
            scrollbarWidth: 'thin',
            scrollbarColor: `${alpha(golden, 0.35)} transparent`,
            '*': { scrollbarColor: `${alpha(golden, 0.35)} transparent` },
          } : {
            '::-webkit-scrollbar':        { width: 0, height: 0, background: 'transparent' },
            '::-webkit-scrollbar-track':  { background: 'transparent' },
            '::-webkit-scrollbar-thumb':  { background: 'transparent' },
            scrollbarWidth: 'none',
            '*': { scrollbarWidth: 'none' },
            '*::-webkit-scrollbar': { display: 'none' },
          }),
          '::selection': {
            background: goldenSubtle,
            color: golden,
          },
        },
      },

      // ── Button ───────────────────────────────────────────────────────────────
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 8,
            letterSpacing: '0.01em',
            transition: 'all 0.18s ease',
            '&:focus-visible': {
              outline: `2px solid ${alpha(golden, 0.6)}`,
              outlineOffset: 2,
            },
          },
          contained: {
            background: goldenGrad,
            // Texto/íconos SOBRE el acento: lo decide `brand.onAccent` de cada marca —
            // azul muy oscuro sobre el cian de Booster, oscuro sobre el dorado de Imperium.
            color: isDark ? P.brand.onAccent : '#ffffff',
            // Corner radius homologado en TODOS los botones primarios (referencia "+ Nuevo Cliente").
            borderRadius: 20,
            boxShadow: `0 2px 8px ${alpha(golden, 0.28)}`,
            '& .MuiButton-startIcon, & .MuiButton-endIcon': { color: 'inherit' },
            '&:hover': {
              background: goldenGradSoft,
              boxShadow: `0 4px 16px ${alpha(golden, 0.4)}`,
              transform: 'translateY(-1px)',
            },
            '&:active': { transform: 'translateY(0)' },
          },
          outlined: {
            borderColor: goldenBorder,
            color: golden,
            '&:hover': {
              borderColor: golden,
              background: goldenSubtle,
            },
          },
          text: {
            color: text.secondary,
            '&:hover': {
              // Fondo dorado más visible + texto oscuro para contraste fuerte
              background: alpha(P.brand.golden, 0.22),
              color: isDark ? P.text.inverse : '#0A0A0A',
            },
          },
        },
      },

      // ── Card ─────────────────────────────────────────────────────────────────
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            border: `1px solid ${border.subtle}`,
            // Sheen glass también en claro: MISMA estructura/ángulo que el oscuro
            // (P.gradients.cardSheen = 135deg, acento → transparente al 60%) pero más marcado
            // para que se note sobre blanco. Esas "líneas transversales" = esta banda diagonal.
            backgroundImage: isDark
              ? P.gradients.cardSheen
              : `linear-gradient(135deg, ${alpha(golden, 0.14)} 0%, transparent 60%)`,
            backgroundColor: bg.paper,
            boxShadow: shadow.md,
            transition: 'box-shadow 0.2s ease, border-color 0.2s ease, transform 0.22s ease, background-color 0.25s ease',
            '&:hover': {
              // Booster: glow emerald + borde teñido (instrumento). Imperium: hover neutro.
              boxShadow: isBooster
                ? `${shadow.lg}, 0 0 0 1px ${alpha(golden, 0.16)}, 0 16px 36px -18px ${alpha(golden, 0.30)}`
                : shadow.lg,
              borderColor: isBooster ? alpha(golden, 0.32) : border.default,
            },
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: { '&:last-child': { paddingBottom: 20 } },
        },
      },

      // ── Paper ────────────────────────────────────────────────────────────────
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${border.subtle}`,
            transition: 'background-color 0.25s ease, border-color 0.2s ease',
          },
          elevation1: { boxShadow: shadow.sm },
          elevation2: { boxShadow: shadow.md },
          elevation3: { boxShadow: shadow.lg },
          elevation4: { boxShadow: shadow.xl },
        },
      },

      // ── Drawer ───────────────────────────────────────────────────────────────
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            backgroundColor: bg.paper,
            borderRight: `1px solid ${border.golden}`,
            boxShadow: 'none',
            transition: 'background-color 0.25s ease',
          },
        },
      },

      // ── AppBar ───────────────────────────────────────────────────────────────
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: bg.glass,
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            borderBottom: `1px solid ${border.golden}`,
            boxShadow: 'none',
            transition: 'background-color 0.25s ease, border-color 0.2s ease',
          },
        },
      },

      // ── TextField / Input ────────────────────────────────────────────────────
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            transition: 'border-color 0.15s, box-shadow 0.15s',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: border.default,
              transition: 'border-color 0.15s',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: border.strong,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: golden,
              borderWidth: 1,
            },
            '&.Mui-focused': {
              boxShadow: `0 0 0 3px ${goldenGlow}`,
            },
          },
        },
      },

      // ── Select ───────────────────────────────────────────────────────────────
      MuiSelect: {
        styleOverrides: {
          icon: { color: text.secondary },
        },
      },

      // ── Chip ─────────────────────────────────────────────────────────────────
      MuiChip: {
        styleOverrides: {
          // Booster: cada pill mantiene SU color pero con el estilo glass del FAB
          // (relleno del color semi-transparente + borde + texto del color).
          // El color 'default' queda gris-glass (como la pill de "Fuente").
          // Imperium (público) mantiene su look original por color.
          root: ({ ownerState, theme }: any) => {
            const base = { borderRadius: 6, fontWeight: 600, fontSize: 12, height: 24 };
            const c = ownerState?.color;
            if (isBooster && c && c !== 'default' && theme.palette[c]?.main) {
              const main = theme.palette[c].main as string;
              return {
                ...base,
                backgroundColor: alpha(main, 0.16),
                border: `1px solid ${alpha(main, 0.45)}`,
                color: main,
                '& .MuiChip-label':      { color: main },
                '& .MuiChip-icon':       { color: main },
                '& .MuiChip-deleteIcon': { color: alpha(main, 0.7), '&:hover': { color: main } },
              };
            }
            return base;
          },
          colorDefault: {
            backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
            color: text.secondary,
            border: `1px solid ${border.subtle}`,
          },
        },
      },

      // ── Avatar ───────────────────────────────────────────────────────────────
      MuiAvatar: {
        styleOverrides: {
          root: {
            background: goldenGrad,
            color: isDark ? P.text.inverse : '#ffffff',
            fontWeight: 700,
          },
        },
      },

      // ── Divider ──────────────────────────────────────────────────────────────
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: border.subtle },
        },
      },

      // ── ListItemButton ───────────────────────────────────────────────────────
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            transition: 'background 0.15s, color 0.15s',
            '&:hover': { backgroundColor: alpha(golden, 0.06) },
            '&.Mui-selected': {
              backgroundColor: goldenSubtle,
              color: golden,
              '&:hover': { backgroundColor: alpha(golden, 0.14) },
            },
          },
        },
      },

      // ── Tooltip ──────────────────────────────────────────────────────────────
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? alpha('#0E1411', 0.92) : '#1F2421',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            border: `1px solid ${alpha(golden, isDark ? 0.18 : 0.28)}`,
            color: isDark ? text.primary : '#F0EDE6',
            fontSize: 12,
            fontWeight: 500,
            borderRadius: 8,
            padding: '6px 10px',
            boxShadow: shadow.lg,
          },
          arrow: {
            color: isDark ? alpha('#0E1411', 0.92) : '#1F2421',
            '&::before': { border: `1px solid ${alpha(golden, isDark ? 0.18 : 0.28)}` },
          },
        },
        defaultProps: { arrow: true },
      },

      // ── Dialog ───────────────────────────────────────────────────────────────
      MuiDialog: {
        styleOverrides: {
          paper: {
            position: 'relative',
            borderRadius: 16,
            border: `1px solid ${border.default}`,
            backgroundImage: 'none',
            backgroundColor: bg.elevated,
            boxShadow: shadow.xl,
            ...(isBooster ? {
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0, left: 16, right: 16, height: '1px',
                background: `linear-gradient(90deg, transparent, ${alpha(golden, 0.6)}, transparent)`,
                pointerEvents: 'none',
              },
            } : {}),
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: { fontWeight: 700, fontSize: 16, paddingBottom: 8 },
        },
      },

      // ── Popover ──────────────────────────────────────────────────────────────
      MuiPopover: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
            border: `1px solid ${border.default}`,
            backgroundImage: 'none',
            backgroundColor: bg.elevated,
            boxShadow: shadow.xl,
          },
        },
      },

      // ── Menu ─────────────────────────────────────────────────────────────────
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
            border: `1px solid ${border.default}`,
            backgroundImage: 'none',
            backgroundColor: bg.elevated,
            boxShadow: shadow.xl,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            margin: '0 4px',
            fontSize: 14,
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
            },
            '&.Mui-selected': {
              backgroundColor: goldenSubtle,
              color: golden,
            },
          },
        },
      },

      // ── DataGrid (MUI X) ─────────────────────────────────────────────────────
      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: 'none',
            '--DataGrid-rowBorderColor': border.subtle,
          },
          columnHeaders: {
            backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
            borderBottom: `1px solid ${border.default}`,
          },
          columnHeaderTitle: {
            fontWeight: 700,
            fontSize: 12,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: text.secondary,
          },
          row: {
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.025)',
            },
          },
          cell: {
            borderBottom: `1px solid ${border.subtle}`,
          },
          footerContainer: {
            borderTop: `1px solid ${border.default}`,
            // Espacio a la derecha para que los controles de paginación no queden
            // tapados por el FAB flotante de Gustavo (fixed, bottom/right). Afecta a
            // todas las listas con DataGrid. Ver bug 6a3ed141 (no se podía pasar de página).
            paddingRight: 72,
          },
        },
      } as any,

      // ── Tab ──────────────────────────────────────────────────────────────────
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 14,
            minHeight: 44,
            transition: 'color 0.18s ease',
            '&.Mui-selected': { color: golden },
            '&:hover': { color: isDark ? text.primary : '#000' },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: golden,
            height: 3,
            borderRadius: 3,
            boxShadow: isBooster ? `0 0 8px ${alpha(golden, 0.55)}` : 'none',
          },
        },
      },

      // ── Switch ───────────────────────────────────────────────────────────────
      MuiSwitch: {
        styleOverrides: {
          root: {
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: golden,
              '& + .MuiSwitch-track': {
                backgroundColor: isDark ? P.brand.goldenMid : P.brand.goldenDark,
                opacity: 0.8,
              },
            },
          },
        },
      },

      // ── Badge ────────────────────────────────────────────────────────────────
      MuiBadge: {
        styleOverrides: {
          badge: { fontWeight: 700, fontSize: 10 },
        },
      },

      // ── Skeleton ─────────────────────────────────────────────────────────────
      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: isBooster
              ? alpha(golden, isDark ? 0.07 : 0.10)
              : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'),
            ...(isBooster ? { '&::after': { background: `linear-gradient(90deg, transparent, ${alpha(golden, 0.10)}, transparent)` } } : {}),
          },
        },
      },

      // ── Alert ────────────────────────────────────────────────────────────────
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            border: '1px solid',
          },
          standardSuccess: {
            backgroundColor: 'rgba(34,197,94,0.08)',
            borderColor: 'rgba(34,197,94,0.20)',
            color: isDark ? '#4ade80' : '#15803d',
          },
          standardError: {
            backgroundColor: 'rgba(239,68,68,0.08)',
            borderColor: 'rgba(239,68,68,0.20)',
            color: isDark ? '#f87171' : '#b91c1c',
          },
          standardWarning: {
            backgroundColor: 'rgba(245,158,11,0.08)',
            borderColor: 'rgba(245,158,11,0.20)',
            color: isDark ? '#fbbf24' : '#b45309',
          },
          standardInfo: {
            backgroundColor: 'rgba(59,130,246,0.08)',
            borderColor: 'rgba(59,130,246,0.20)',
            color: isDark ? '#60a5fa' : '#1d4ed8',
          },
        },
      },
    },
  });
}
